'use client';
import clsx from 'clsx';
import { useEffect, useRef, useMemo } from 'react';
import { useBetslipStore } from '@/store/betslip';
import { useSiteStore } from '@/store/site';
import { RefreshIcon, ViewMyBetIcon } from '@/components/icons/svg-icons';
import { BookingConfirmContainer } from '../betslip-booking/components/booking-confirm-container';
import { ConfirmBetTicket } from './components/confirm-bet-ticket';
import { useOuterClick } from '@nf/utils-common/hooks';
import { Notice } from '../betslip-container/components/bet-msg/notice';
import { useLocale, useTranslations } from 'next-intl';
import { useBetslipContext } from '../contexts/betslip-context';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { usePathname, useRouter } from '@/navigation';
import { confirmBetStyle } from './confirm-bet.style';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useApiBackendInfo, useUnsettledStatusCountInfo } from '@/hooks/query';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import { routerService } from '@/services/router.service';
import { KafkaNote } from '@nf/utils-common/constants';
import { ConfirmBetTicketSmp } from './components/confirm-bet-ticket-smp';
import { useAnalyseAttribute, useAnalyseAttributeWithToggle } from '@/features/next-analyse';
import { useFloatBetslip } from '../hook/use-float-betslip';
import { useAsideBetslip } from '../hook/use-aside-betslip';
import { ConfirmBetModalHeader } from '../betslip-header/confirm-bet-modal-header';
import { modalService } from '@/services/modal.service';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';
import { fetchGroupByMatchIds, useGoldenDuelSetting } from '@/hooks/query/golden-duel';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { ApiResponse } from '@nf/types/api';
import { GroupByMatchIdsData } from '@/hooks/query/golden-duel/types';

export const ConfirmBet = () => {
	const t = useTranslations('General');
	const { oddsType, defaultStakeId, inpbetStake, isIndependentMode } = useBetslipContext();
	const router = useRouter();
	const pathName = usePathname();
	const locale = useLocale();
	const view = useAppearanceStore(state => state.view);
	const isParlayMode = useSiteStore(state => state.isParlayMode);
	const { activeFloatBetSlip } = useFloatBetslip()
	const { activeAsideBetSlip } = useAsideBetslip();
	const { statusCount } = useUnsettledStatusCountInfo(1); // auto use 1

	const [
		isExpand,
		currentMultiTickets,
		currentParlayTicketModel,
		isBookingBet,
		betAgain,
		removeAllTicket,
		setIsConfirmBet,
		setIsBookingBet,
		canBetResult,
		isConfirmBetSimpleMode,
		getTotalBet,
		getTotalStake,
		initConfirmBetSimpleMode,
		toggleIsConfirmBetSimpleMode,
		toggleIsExpand
	] = useBetslipStore(state => [
		state.isExpand,
		state.currentMultiTickets,
		state.currentParlayTicketModel,
		state.isBookingBet,
		state.betAgain,
		state.removeAllTicket,
		state.setIsConfirmBet,
		state.setIsBookingBet,
		state.canBetResult,
		state.isConfirmBetSimpleMode,
		state.getTotalBet,
		state.getTotalStake,
		state.initConfirmBetSimpleMode,
		state.toggleIsConfirmBetSimpleMode,
		state.toggleIsExpand
	]);

	const parlayTransId = useMemo(() => {
		if (currentParlayTicketModel?.TransId_Combo != '0') {
			return currentParlayTicketModel?.TransId_Combo as string;
		} else if (currentParlayTicketModel?.TransId_System != '0') {
			return currentParlayTicketModel?.TransId_System as string;
		} else if (currentParlayTicketModel?.TransId_Lucky != '0') {
			return currentParlayTicketModel?.TransId_Lucky as string;
		}
	}, [currentParlayTicketModel?.TransId_Combo, currentParlayTicketModel?.TransId_Lucky, currentParlayTicketModel?.TransId_System]);

	useEffect(() => {
		initConfirmBetSimpleMode(view);
	}, []);

	const selectRef = useRef<HTMLDivElement>(null);

	// 若為float模式需要關閉bet slip modal
	const closeBetSlipModal = () => {
		if (activeFloatBetSlip && isExpand) {
			toggleIsExpand();
		}
	}

	const closeConfirmBet = () => {
		closeBetSlipModal();
		removeAllTicket();
		setIsConfirmBet(false);
		setIsBookingBet(false);
		if (goldenDuelStorageService.shouldShowTooltip()) {
			modalService.openGoldenDuelToolTipModal(true);
		}
	}

	useOuterClick(selectRef, closeConfirmBet);
	const betAgainTagged = useAnalyseAttributeWithToggle(!!parlayTransId, ['use-parlay-confirm-bet', 'use-single-confirm-bet']);
	const toMyBetTagged = useAnalyseAttribute('switch-to-statement-via-confirm-bet');

	const isGalaxy = view === ViewEnum.galaxy;
	const setKafka = (action: number) => {
		const kafkaData = {
			note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1,
			type: isGalaxy ? 8 : (isParlayMode ? 11 : 10),
			action
		}
		setKafkaNormal(kafkaData);
	}

	const clickBetAgain = () => {
		setKafka(isGalaxy ? 5 : (isParlayMode ? 4 : 7));
		betAgain(oddsType, defaultStakeId, inpbetStake, view);
	}

	const clickToMyBet = () => {
		setKafka(isGalaxy ? 6 : (isParlayMode ? 5 : 8));
		routerService.push(router.push, locale, '/bets/u', pathName);
		closeConfirmBet();
		removeAllTicket();
		setIsConfirmBet(false);
		setIsBookingBet(false);
	}

	const clickToggleSimpleMode = () => {
		setKafka(isConfirmBetSimpleMode ? 8 : 7); // 7: switch on, 8: switch off
		toggleIsConfirmBetSimpleMode();
	}

	const { isGoldenDuelSupported, data: goldenDuelSetting } = useGoldenDuelSetting();
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const totalBet = getTotalBet(isIndependentMode, true);
	const isFloatStyle = activeAsideBetSlip || activeFloatBetSlip;
	const apiBackendInfo = useApiBackendInfo();

	useEffect(() => {
		if (!isBookingBet && currentMultiTickets.length > 0 && (!parlayTransId || parlayTransId === '0') && SiteConfigsData?.EnableGoldenDuel && goldenDuelSetting) {
			const goldenDuelMatchIds = currentMultiTickets
				.filter(ticket => ticket.TransId && ticket.stake && !smpService.isSmpTicket(ticket))
				.map(ticket => {
					const currencySetting = goldenDuelSetting?.CurrencySettings.find(setting => setting.CurrencyId === user?.CurrId);
					const awayCount = Math.ceil(Math.max(0, (currencySetting?.TargetAmount ?? 0) - ticket.stake));
					if (ticket.TransId && ticket.TransId !== '0' && ticket.CustomCurrencyName !== 'SBC' && isGoldenDuelSupported(ticket.SportType, ticket.LeagueId) && awayCount === 0) {
						goldenDuelStorageService.addTransactionId(ticket.TransId);
						return ticket.ParentMatchid > 0 ? ticket.ParentMatchid : ticket.Matchid;
					}
					return 0;
				});

			const filteredGoldenDuelMatchIds = new Set([...goldenDuelMatchIds.filter(id => id !== 0)]);
			if (filteredGoldenDuelMatchIds.size > 0) {
				fetchGroupByMatchIds(apiBackendInfo, Array.from(filteredGoldenDuelMatchIds)).then((response: ApiResponse<GroupByMatchIdsData[]>) => {
					const drawTimeDatas = response.Data;
					if (drawTimeDatas) {
						const filterEmptyDrawTimeDatas = drawTimeDatas.filter(item => item.DrawTime !== null);
						if (filterEmptyDrawTimeDatas.length > 0) {
							const maxDrawTime = filterEmptyDrawTimeDatas
								.reduce((max, current) => {
									return new Date(current.DrawTime) > new Date(max.DrawTime) ? current : max;
								});

							goldenDuelStorageService.setDrawTime(maxDrawTime.DrawTime);
						}
					}
				});
			}
		}
	}, []);

	return (
		<>
			<section
				ref={selectRef}
				className={clsx(
					'confirm-bet',
					confirmBetStyle.root,
					{ [confirmBetStyle.confirmBetModal]: activeAsideBetSlip || activeFloatBetSlip },
				)}
			>
				{view !== ViewEnum.galaxy || isFloatStyle ? <ConfirmBetModalHeader /> : null}
				<div className={clsx(
					confirmBetStyle.confirmBetScrollbar,
					{ [confirmBetStyle.confirmBetParlayScrollbar]: activeAsideBetSlip || activeFloatBetSlip }
				)}
				>
					<div className={clsx({
						[confirmBetStyle.confirmBetWrap]: !isFloatStyle,
						[confirmBetStyle.confirmParlayBetWrap]: isFloatStyle
					})}
					>
						<div className="confirm-bet__container" data-simple={isConfirmBetSimpleMode}>
							<div className={clsx({ 'scrollbar': !(activeAsideBetSlip || activeFloatBetSlip) })}>
								{isBookingBet ? (
									<BookingConfirmContainer />
								) : (
									<>
										{currentMultiTickets
											.filter(ticket => ticket.TransId && ticket.stake)
											.map((ticket, index) => {
												const key = `${ticket.OddsID}-${index}`;
												return smpService.isSmpTicket(ticket) ? (
													<ConfirmBetTicketSmp
														key={key}
														ticket={ticket}
													/>
												) : (
													<ConfirmBetTicket
														key={key}
														isParlay={false}
														tickets={[ticket]}
														transId={ticket.TransId as string}
													/>
												);
											})}

										{currentParlayTicketModel != null && parlayTransId ? (
											<ConfirmBetTicket
												key={currentParlayTicketModel.CanBetTicketCnt}
												isParlay={true}
												tickets={currentParlayTicketModel.Tickets}
												comboData={currentParlayTicketModel.comboData}
												transId={parlayTransId}
											/>
										) : null}
									</>
								)}
							</div>
						</div>
						{
							canBetResult.confirmBetMsg.length > 0 ? canBetResult.confirmBetMsg.map((c, idx) => (
								<Notice
									key={idx}
									code={c.code}
									msg={c.msg}
									status={c.cssStatus}
								/>
							)) : null
						}
						{
							canBetResult.confirmBetMsg.length > 0 ? canBetResult.confirmBetMsg.map((c, idx) => (
								<Notice
									key={idx}
									code={c.code}
									msg={c.msg}
									status={c.cssStatus}
								/>
							)) : null
						}
						{
							totalBet > 1 ? (
								<div className={confirmBetStyle.bottomInfo}>
									<div className={clsx(betslipStyle.textInfo, betslipStyle.textInfoMars)}>
										{t('lbl_TotalBets')}:
										<span className={betslipStyle.textSecondary}>{totalBet}</span>
									</div>
									<div className={clsx(betslipStyle.textInfo, betslipStyle.textInfoMars)}>
										{t('lbl_TotalStakes')}:
										<span className={betslipStyle.textSecondary}>{getTotalStake(isIndependentMode, true)}</span>
									</div>
								</div>
							) : null
						}
						<div className="confirm-bet__footer">
							<button
								className={clsx('btn', confirmBetStyle.button)}
								onClick={clickBetAgain}
								ref={betAgainTagged}
							>
								<RefreshIcon />
								<span className="btn__text">{t('lbl_BetAgain')}</span>
							</button>
							<button
								className={clsx('btn', confirmBetStyle.button)}
								onClick={clickToMyBet}
								ref={toMyBetTagged}
							>
								<ViewMyBetIcon />
								<span className="btn__text">{view === ViewEnum.mars ? t('lbl_CheckStatement') : t('btn_ViewMyBets')}</span>
								{
									view === ViewEnum.mars && statusCount?.WC
										? <div className={clsx('badge', 'badge--default', confirmBetStyle.badge)}>{statusCount?.WC}</div>
										: null
								}

							</button>
						</div>
						<div className={clsx('confirm-bet__info', confirmBetStyle.info)}>
							{t('lbl_ShowSimpleConfirmBet')}
							<div
								className="switch"
								onClick={clickToggleSimpleMode}
								data-active={isConfirmBetSimpleMode}
							>
								<div className="switch__thumb" />
							</div>
						</div>
					</div>
				</div>

			</section>
			<div className={clsx('confirm-bet-backdrop', { [confirmBetStyle.backdrop]: activeAsideBetSlip || activeFloatBetSlip })}></div>
		</>
	);
};
